import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ReportsService from '../../../Services/reports.service';

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.webp,.tif,.tiff';

/**
 * Uploads a Tax Authority VAT summary report ("סיכומי דיווח") and hands the periods it
 * contains back to the caller, already aligned to the client's reporting grid by the server.
 */
const YearlyVatReportImport = ({ t, caseId, token, year, onPeriodsParsed }) => {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isParsing, setIsParsing] = useState(false);
    const [warnings, setWarnings] = useState([]);
    const [error, setError] = useState(null);

    const selectFile = (selected) => {
        if (!selected) return;
        setFile(selected);
        setWarnings([]);
        setError(null);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        selectFile(event.dataTransfer?.files?.[0]);
    };

    const handleParse = async () => {
        if (!file) {
            toast.info(t('SelectVatReportFileFirst'));
            return;
        }

        setIsParsing(true);
        setWarnings([]);
        setError(null);

        try {
            const result = await ReportsService.parseVatReportDocument(caseId, file, token, year);

            if (!result?.success) {
                setError(result?.error || t('FailedToReadVatReportFile'));
                return;
            }

            const nextWarnings = [];
            if (result.reportingTypeMismatch) {
                nextWarnings.push(
                    t('VatReportReportingTypeMismatch', {
                        documentType: result.documentReportingType || t('Unknown'),
                        clientType: result.clientReportingType || t('Unknown'),
                    })
                );
            }
            if (result.vatNumberMismatch) {
                nextWarnings.push(
                    t('VatReportVatNumberMismatch', { vatNumber: result.documentVatNumber || '' })
                );
            }
            if (result.year && year && result.year !== year) {
                nextWarnings.push(t('VatReportYearMismatch', { documentYear: result.year, selectedYear: year }));
            }
            setWarnings(nextWarnings);

            const filledCount = onPeriodsParsed(result);
            toast.success(t('VatReportFileReadSuccess', { count: filledCount }));
        } catch (parseError) {
            setError(parseError?.message || t('FailedToReadVatReportFile'));
        } finally {
            setIsParsing(false);
        }
    };

    return (
        <div className="yvr__alerts yvr__no-print">
            <div
                className={`yvr__dropzone ${isDragging ? 'yvr__dropzone--active' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
            >
                <span className="yvr__dropzone-title">{t('UploadTaxAuthorityVatReport')}</span>
                <span className="yvr__dropzone-hint">{t('UploadTaxAuthorityVatReportHint')}</span>
                {file && <span className="yvr__dropzone-file">{file.name}</span>}
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    style={{ display: 'none' }}
                    onChange={(event) => {
                        selectFile(event.target.files?.[0]);
                        event.target.value = '';
                    }}
                />
            </div>

            <div className="yvr__footer">
                <span className="yvr__manual-hint">{t('VatReportAutoFillExplanation')}</span>
                <button
                    type="button"
                    className="yvr__btn yvr__btn--primary"
                    onClick={handleParse}
                    disabled={!file || isParsing}
                >
                    {isParsing ? t('ReadingFile') : t('ReadFileAndFillPeriods')}
                </button>
            </div>

            {warnings.map((warning, index) => (
                <div key={index} className="yvr__alert yvr__alert--warning">{warning}</div>
            ))}
            {error && <div className="yvr__alert yvr__alert--error">{error}</div>}
        </div>
    );
};

export default YearlyVatReportImport;
